import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { LinkBase, useNotify, useTranslate } from 'ra-core';
import { useGenerateInsightsForUnit } from '../../../api/hooks/useGenerateInsightsForUnit';
import { LlmFilesDropzone } from './LlmFilesDropzone';
import { Button, buttonVariants } from '../../../components/ui/button';
import { Loader2 } from 'lucide-react';
import { cn } from '../../../lib/utils';

type GenerateInsightsControlProps = {
  unitId: string;
  insightId?: string | number;
  setPendingLlmInsights: (pending: boolean) => void;
  onTextStream: (chunk: string) => void;
  onTextStreamEnd: () => void;
};

export const GenerateInsightsControl = ({
  unitId,
  insightId,
  setPendingLlmInsights,
  onTextStream,
  onTextStreamEnd,
}: GenerateInsightsControlProps) => {
  const [files, setFiles] = useState<File[]>([]);
  const notify = useNotify();
  const translate = useTranslate();
  const generateInsightsForUnit = useGenerateInsightsForUnit();

  const generateInsights = useMutation({
    mutationFn: async () => {
      try {
        setPendingLlmInsights(true);
        const lines = await generateInsightsForUnit.mutateAsync({
          unitId,
          files,
        });

        for await (const insight of lines) {
          if (insight.type === 'data') {
            onTextStream(insight.data.text);
            setPendingLlmInsights(false);
          } else {
            console.error(insight.error);
            if (typeof insight.error === 'string') {
              throw new Error(insight.error);
            } else {
              throw new Error(
                translate('moonstar.unitPerformance.unknownError')
              );
            }
          }
        }

        setFiles([]);
      } finally {
        setPendingLlmInsights(false);
        onTextStreamEnd();
      }
    },
    onError(error) {
      notify(
        error instanceof Error
          ? translate(
              'moonstar.unitPerformance.aiInsights.generateFailedWithReason',
              {
                error: error.message,
              }
            )
          : translate('moonstar.unitPerformance.aiInsights.generateFailed'),
        { type: 'error' }
      );
    },
  });

  const isGeneratingInsights = generateInsights.isPending;

  return (
    <div className="border-t px-4 py-3">
      <LlmFilesDropzone
        files={files}
        onFilesChange={setFiles}
        disabled={isGeneratingInsights}
      />

      {/* Chat and Generate Insights Buttons */}
      <div className="flex w-full flex-col gap-2 md:flex-row">
        <LinkBase
          to={`/units/${unitId}/performance/insights/${insightId}/chat`}
          aria-disabled={!insightId || isGeneratingInsights}
          tabIndex={!insightId || isGeneratingInsights ? -1 : undefined}
          className={cn(
            buttonVariants({ variant: 'default' }),
            (!insightId || isGeneratingInsights) &&
              'pointer-events-none opacity-50'
          )}
        >
          {translate('moonstar.unitPerformance.aiInsights.chat')}
        </LinkBase>
        <Button
          variant="default"
          onClick={() => generateInsights.mutate()}
          disabled={isGeneratingInsights}
          className="md:ml-auto"
        >
          {isGeneratingInsights ? <Loader2 className="animate-spin" /> : null}
          {translate(
            isGeneratingInsights
              ? 'moonstar.unitPerformance.aiInsights.generating'
              : 'moonstar.unitPerformance.aiInsights.generate'
          )}
        </Button>
      </div>
    </div>
  );
};
